import type { SxProps } from '@mui/material';
import { ClickAwayListener, InputAdornment, Tooltip } from '@mui/material';
import { IconButton } from 'design-system';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { MdInfo } from 'react-icons/md';

const tooltipSlotProps = {
  tooltip: {
    sx: {
      marginBottom: '4px !important',
      marginLeft: 2,
      marginRight: 2,
    } as SxProps,
  },
};

export interface DisabledFieldTooltipProps {
  variant: 'desktop' | 'adornment';
  title: string;
  children?: ReactNode;
}

/**
 * Shows the full value of a disabled field in a tooltip.
 * - **Desktop**: Wraps children; hover shows tooltip.
 * - **Adornment**: Renders an info icon inside the field; click shows tooltip (mobile).
 */
export function DisabledFieldTooltip(props: DisabledFieldTooltipProps) {
  const [adornmentOpen, setAdornmentOpen] = useState(false);

  const { variant, title, children } = props;

  if (!title) return null;

  if (variant === 'desktop') {
    return (
      <Tooltip title={title} placement="top" enterDelay={400} arrow slotProps={tooltipSlotProps}>
        <span style={{ display: 'inline-block', width: '100%' }}>{children}</span>
      </Tooltip>
    );
  }

  return (
    <InputAdornment position="end">
      <ClickAwayListener onClickAway={() => setAdornmentOpen(false)}>
        <div>
          <Tooltip
            open={adornmentOpen}
            title={title}
            placement="top"
            arrow
            disableFocusListener
            disableTouchListener
            slotProps={tooltipSlotProps}
          >
            <IconButton
              track={false}
              size="small"
              onClick={() => setAdornmentOpen((prev) => !prev)}
              aria-label={adornmentOpen ? 'Hide full text' : 'Show full text'}
              sx={{ padding: 0.5 }}
            >
              <MdInfo size={20} />
            </IconButton>
          </Tooltip>
        </div>
      </ClickAwayListener>
    </InputAdornment>
  );
}
